import { Box, Chip, Divider, Typography, useMediaQuery, useTheme } from '@material-ui/core'
import { CalendarToday, Schedule } from '@material-ui/icons'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SettlementsKPIsResponseModel } from '../../../../api/Argentia/endpoints/settlements/model/settlements'
import { EnumTipoCicloFacturacion } from '../../../../api/oData'
import { formatDate } from '../../../../helpers/formatDateHelper'
import { CycleExecutionModel } from '../../../invoiceCycles/invoiceCycles.model'
import { fetchInvoiceCycleExecution } from '../../../invoiceCycles/invoiceCycles.service'
import { SettlementDetailKpiItem } from '../settlementDetailKpiItem/settlementDetailKpiItem'
import { SettlementKpiItem } from '../settlementKpiItem/settlementKpiItem'
import { SettlementKpiType } from '../settlementKpiRightContent/settlementKpiRightContent'

interface SettlementKpiLeftContentProps {
  kpis?: SettlementsKPIsResponseModel
  isLoading: boolean
  invoiceCycleType?: EnumTipoCicloFacturacion
  executionCycleId?: number
  invoiceCycleDescription?: string
  getExecutionState?: (status: number) => void
  /** KPI actualmente seleccionado */
  activeKpi?: SettlementKpiType | null
  /** Handler al hacer click en un KPI */
  onKpiClick?: (kpiType: SettlementKpiType) => void
}

export const SettlementKpiLeftContent: React.FC<SettlementKpiLeftContentProps> = ({
  kpis,
  isLoading,
  invoiceCycleType,
  executionCycleId,
  invoiceCycleDescription,
  getExecutionState,
  activeKpi,
  onKpiClick
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'settlements'])
  const [executionData, setExecutionData] = useState<CycleExecutionModel | null>(null)
  const [isFetchingExecution, setIsFetchingExecution] = useState(false)
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))

  useEffect(() => {
    const fetchExecution = async () => {
      if (executionCycleId) {
        setIsFetchingExecution(true)
        const response = await fetchInvoiceCycleExecution(executionCycleId)
        if (response.success && response.data) {
          setExecutionData(response.data)
          getExecutionState?.(response.data.status)
        }
        setIsFetchingExecution(false)
      }
    }
    fetchExecution()
  }, [executionCycleId])

  const getDetailText = () => {
    if (!executionData || !invoiceCycleType) {
      return null
    }

    const executionDate = executionData.executionDate
      ? formatDate(executionData.executionDate, 'DD/MM/YYYY - HH:mm:ss')
      : '--/--/---- - --:--:--'

    const cycleType = t(
      `enums:EnumTipoCicloFacturacion.${EnumTipoCicloFacturacion[invoiceCycleType]}`
    )

    const startDate = executionData.startDate
      ? formatDate(executionData.startDate, 'DD/MM/YYYY')
      : '--/--/----'

    const endDate = executionData.endDate
      ? formatDate(executionData.endDate, 'DD/MM/YYYY')
      : '--/--/----'

    return (
      <Box>
        <Box display="flex" alignItems="center" mb={1}>
          <Schedule fontSize="small" style={{ marginRight: 8, color: '#757575' }} />
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {t('settlements:components.kpiCard.kpiItems.totalSettlements.executionLabel')}:{' '}
            <span style={{ fontWeight: 400 }}>{executionDate}</span>
          </Typography>
        </Box>
        <Divider style={{ margin: '8px 0' }} />
        <Box display="flex" alignItems="center" mb={1}>
          <Typography variant="body2" style={{ fontWeight: 500, marginRight: 8 }}>
            {t('settlements:components.kpiCard.kpiItems.totalSettlements.cycleTypeLabel')}:
          </Typography>
          <Chip
            label={cycleType}
            size="small"
            color="primary"
            variant="outlined"
            style={{ fontWeight: 500 }}
          />
        </Box>
        <Divider style={{ margin: '8px 0' }} />
        <Box display="flex" alignItems="center">
          <CalendarToday fontSize="small" style={{ marginRight: 8, color: '#757575' }} />
          <Typography variant="body2" style={{ fontWeight: 500 }}>
            {t('settlements:components.kpiCard.kpiItems.totalSettlements.periodLabel')}:{' '}
            <span style={{ fontWeight: 400 }}>
              {startDate} - {endDate}
            </span>
          </Typography>
        </Box>
      </Box>
    )
  }

  const detailContent = getDetailText()

  return (
    <Box style={{ height: '100%' }} display="flex" flexDirection="column">
      <Box
        style={{ height: '100%', columnGap: 8 }}
        display="flex"
        flexDirection={isSmallScreen ? 'column' : 'row'}
        justifyContent={isSmallScreen ? 'space-evenly' : 'center'}
        alignItems="stretch"
        flexGrow={1}
      >
        <Box
          style={{
            flex: isSmallScreen ? undefined : '0 0 auto',
            display: 'flex',
            height: '100%',
            width: isSmallScreen ? '100%' : 'auto',
            flexDirection: 'column',
            rowGap: 16
          }}
        >
          <SettlementKpiItem
            id="all"
            isLoading={isLoading}
            title={t('settlements:components.kpiCard.kpiItems.totalSettlements.title')}
            value={(kpis?.totalSettlements || 0) - (kpis?.totalInvoicedSettlements || 0)}
            description={t('settlements:components.kpiCard.kpiItems.totalSettlements.description')}
            onClick={onKpiClick ? () => onKpiClick('all') : undefined}
            isActive={activeKpi === 'all'}
          />
          <SettlementKpiItem
            id="invoicedSettlements"
            isLoading={isLoading}
            title={t('settlements:components.kpiCard.kpiItems.totalInvoicedSettlements.title')}
            value={kpis?.totalInvoicedSettlements}
            description={t(
              'settlements:components.kpiCard.kpiItems.totalInvoicedSettlements.description'
            )}
            disabled
            tooltipString={t(
              'settlements:components.kpiCard.kpiItems.totalInvoicedSettlements.tooltip'
            )}
          />
        </Box>
        <Box
          style={{
            flex: isSmallScreen ? undefined : 1,
            display: 'flex',
            height: '100%',
            width: isSmallScreen ? '100%' : 'auto'
          }}
        >
          <SettlementDetailKpiItem isLoading={isFetchingExecution} detailContent={detailContent} />
        </Box>
      </Box>
    </Box>
  )
}
